import React from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { tableCellClasses } from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import DataList from '../../../common/components/DataList';
import DataTable from '../../../common/components/DataTable';
import DeleteReportIcon from '../../../common/components/icons/DeleteReportIcon';
import EditInterviewIcon from '../../../common/components/icons/EditInterviewIcon';
import { sortItemsOnList } from '../../../common/utils/commonUtils';
import { getNumberInUSFormat } from '../../../common/utils/patientUtils';
import { SortDirs } from '../../../common/utils/sortInfo';
import { TimeTrackerGroup, TimeTrackerResponseItem } from '../../../types/TimeTracker';
import ContextMenu from '../../patient/ContextMenu';

interface TimeTrackerGroupTableProps {
  groups: TimeTrackerGroup[];
  sort: { field: string; dir: string };
  onSorted: (sortField: string) => void;
  editTimeTracker: (timeTracker: TimeTrackerResponseItem) => Promise<void>;
  deleteTimeTracker: (id: number) => void;
}

const StyledDataCell = styled('p')(() => ({
  margin: 0,
}));

const StyledMobileGroupCell = styled(Box)(() => ({
  wordBreak: 'break-word',
}));

export default function TimeTrackerGroupTable({
  groups,
  sort,
  onSorted,
  editTimeTracker,
  deleteTimeTracker,
}: TimeTrackerGroupTableProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // State for child table sorting - separate sort state for each group
  const [childSortOptions, setChildSortOptions] = React.useState<
    Record<string, { sortField: string; sortDir: SortDirs }>
  >({});

  const getGroupKey = (group: TimeTrackerGroup) => {
    return `${group.trackerMonth}-${group.patientId}`;
  };

  const isGroupExpanded = (group: TimeTrackerGroup) => {
    return expandedGroups.has(getGroupKey(group));
  };

  const toggleExpandGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderContextMenu = (item: TimeTrackerResponseItem) => {
    return (
      <ContextMenu
        id={`time-tracker-${item.id}-menu`}
        anchorEl={anchorEl}
        handleClick={handleClick}
        handleClose={handleClose}
        menuItems={[
          {
            isDisplayed: true,
            label: t('timeTracker.edit'),
            icon: <EditInterviewIcon />,
            onClick: () => {
              handleClose();
              // Blur the menu button to prevent focus trap
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
              requestAnimationFrame(() => {
                editTimeTracker(item);
              });
            },
          },
          {
            isDisplayed: true,
            label: t('timeTracker.delete'),
            icon: <DeleteReportIcon />,
            onClick: () => {
              handleClose();
              // Blur the menu button to prevent focus trap
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
              requestAnimationFrame(() => {
                deleteTimeTracker(item.id);
              });
            },
          },
        ]}
      />
    );
  };

  const getSortedTimeTrackers = (group: TimeTrackerGroup): TimeTrackerResponseItem[] => {
    const groupKey = getGroupKey(group);
    const sortOption = childSortOptions[groupKey];

    if (!sortOption) {
      return group.timeTrackers;
    }

    const sortedItems = [...group.timeTrackers];
    sortItemsOnList(sortedItems, sortOption);
    return sortedItems;
  };

  const onChildHeaderClicked = (group: TimeTrackerGroup, headerKey: string) => {
    const groupKey = getGroupKey(group);
    const currentSort = childSortOptions[groupKey];

    const newSortOptions = {
      sortField: headerKey,
      sortDir:
        currentSort && currentSort.sortField === headerKey && currentSort.sortDir === SortDirs.Asc
          ? SortDirs.Desc
          : SortDirs.Asc,
    };

    setChildSortOptions((prev) => ({
      ...prev,
      [groupKey]: newSortOptions,
    }));
  };

  const getIndividualTrackerRows = (timeTrackers: TimeTrackerResponseItem[]) => {
    return timeTrackers.map((item) => [
      { component: <StyledDataCell></StyledDataCell> },
      { component: <StyledDataCell>{item.dateOfService}</StyledDataCell> },
      { component: <StyledDataCell>{item.careTeamMember}</StyledDataCell> },
      { component: <StyledDataCell>{item.role}</StyledDataCell> },
      { component: <StyledDataCell>{item.typeOfService}</StyledDataCell> },
      { component: <StyledDataCell>{getNumberInUSFormat(item.minutes ?? 0)}</StyledDataCell> },
      { component: renderContextMenu(item) },
    ]);
  };

  const renderNestedTable = (group: TimeTrackerGroup) => {
    const groupKey = getGroupKey(group);
    const sortOption = childSortOptions[groupKey];
    const sortedTimeTrackers = getSortedTimeTrackers(group);

    const headers = [
      { text: '', sx: { width: '10%' } },
      {
        text: t('timeTracker.items.dateOfService'),
        sx: { width: '15%' },
        sortKey: 'dateOfService',
      },
      {
        text: t('timeTracker.items.careTeamMember'),
        sx: { width: '20%' },
        sortKey: 'careTeamMember',
      },
      { text: t('timeTracker.items.role'), sx: { width: '15%' }, sortKey: 'role' },
      {
        text: t('timeTracker.items.typeOfService'),
        sx: { width: '20%' },
        sortKey: 'typeOfService',
      },
      { text: t('timeTracker.items.minutes'), sx: { width: '10%' }, sortKey: 'minutes' },
      { text: t('timeTracker.items.action'), sx: { width: '10%' } },
    ];

    return (
      <Box sx={{ mb: 1 }}>
        <DataTable
          commonCellStyles={{
            [`&.${tableCellClasses.head}`]: {
              padding: '12px 8px',
              fontWeight: 600,
            },
            [`&.${tableCellClasses.body}`]: {
              padding: '14px 8px',
            },
          }}
          headers={headers}
          sort={
            sortOption
              ? { field: sortOption.sortField, dir: sortOption.sortDir.toLowerCase() }
              : undefined
          }
          onSorted={(sortKey: string) => onChildHeaderClicked(group, sortKey)}
          items={getIndividualTrackerRows(sortedTimeTrackers)}
        />
      </Box>
    );
  };

  const groupRows = groups.map((group) => [
    {
      component: (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <IconButton
            data-testid={`expand-${getGroupKey(group)}`}
            aria-label="expand row"
            size="small"
            onClick={() => toggleExpandGroup(getGroupKey(group))}
          >
            {isGroupExpanded(group) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
      ),
    },
    {
      component: <Typography>{group.trackerMonth}</Typography>,
    },
    {
      component: <Typography>{group.patientName}</Typography>,
    },
    {
      component: <Typography>{group.externalId}</Typography>,
    },
    {
      component: <Typography>{group.cptCode}</Typography>,
    },
    {
      component: <Typography>{getNumberInUSFormat(group.totalMinutes)}</Typography>,
    },
  ]);

  const groupComponents = groups.map((group) => ({
    component: renderNestedTable(group),
    expand: isGroupExpanded(group),
  }));

  const renderMobileGroups = () => {
    return (
      <DataList
        lineHeight={'auto'}
        data={groups.flatMap((group: TimeTrackerGroup) => {
          const result = group.timeTrackers.map((item: TimeTrackerResponseItem) => {
            const listItems = [
              [
                <>{t('timeTracker.items.dateOfService')}</>,
                <StyledMobileGroupCell>{item.dateOfService}</StyledMobileGroupCell>,
              ],
              [
                <>{t('timeTracker.items.mrn')}</>,
                <StyledMobileGroupCell>{group.externalId}</StyledMobileGroupCell>,
              ],
              [
                <>{t('timeTracker.items.careTeamMember')}</>,
                <StyledMobileGroupCell>{item.careTeamMember}</StyledMobileGroupCell>,
              ],
              [
                <>{t('timeTracker.items.role')}</>,
                <StyledMobileGroupCell>{item.role}</StyledMobileGroupCell>,
              ],
              [
                <>{t('timeTracker.items.typeOfService')}</>,
                <StyledMobileGroupCell>{item.typeOfService}</StyledMobileGroupCell>,
              ],
              [
                <>{t('timeTracker.items.minutes')}</>,
                <StyledMobileGroupCell>
                  {getNumberInUSFormat(item.minutes ?? 0)}
                </StyledMobileGroupCell>,
              ],
            ];

            return {
              leftHeader: (
                <Box>
                  <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>
                    {group.patientName}
                  </Typography>
                  <Typography>
                    {group.trackerMonth} • {getNumberInUSFormat(group.totalMinutes)} min
                  </Typography>
                </Box>
              ),
              rightHeader: renderContextMenu(item),
              items: [...listItems],
            };
          });
          return [...result];
        })}
        customStyle={{
          borderRadius: '16px 16px 0 0',
        }}
      />
    );
  };

  if (!isDesktop) {
    return renderMobileGroups();
  }

  return (
    <DataTable
      sort={{ field: sort.field, dir: sort.dir }}
      onSorted={onSorted}
      commonCellStyles={{
        [`&.${tableCellClasses.head}`]: {
          padding: '16px 8px',
          fontWeight: 600,
        },
        [`&.${tableCellClasses.body}`]: {
          padding: '16px 8px',
        },
      }}
      headers={[
        { text: '', sx: { width: '5%' } },
        {
          text: t('timeTracker.group.trackerMonth'),
          sx: { width: '25%' },
          sortKey: 'trackerMonth',
        },
        { text: t('timeTracker.group.patientName'), sx: { width: '25%' }, sortKey: 'patientName' },
        { text: t('timeTracker.group.externalId'), sx: { width: '20%' }, sortKey: 'externalId' },
        { text: t('timeTracker.group.cptCode'), sx: { width: '10%' }, sortKey: 'cptCode' },
        {
          text: t('timeTracker.group.totalMinutes'),
          sx: { width: '20%' },
          sortKey: 'totalMinutes',
        },
      ]}
      items={groupRows}
      group={groupComponents}
    />
  );
}
